<template>
  <div class="emission-filter">
    <v-radio-group v-model="reactiveValue.filterType" inline class="product-radio" :disabled="!!disabled">
      <div class="radio-part">
        <v-radio
          v-for="(filterType, index) in filterTypeOptions"
          :key="index"
          :label="filterType.name"
          :value="filterType.value"
          :ripple="false"
          @change="handleFilterTypeChanged"
        ></v-radio>
      </div>
    </v-radio-group>
    <div class="emission-filter-select">
      <select-products
        :placeholder="labelFilterPlaceholder.textPlaceholder"
        hasSearch
        :placeHolderInput="labelFilterPlaceholder.textPlaceHolderInput"
        :dataList="filterOptions"
        :itemCount="filterOptions.length - 1"
        :disabled="!!disabled"
        :selectedData="reactiveValue.selectedData"
        :filterType="reactiveValue.filterType"
        @updateProductSelected="handleFilterOptionsSelected"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import i18n from '@/lang/i18n.js';
import { EMISSION_FILTER_TYPES, SORT_CATEGORY_BY_DESC } from '@/constants/products/define-data';
import { getListProductApproved, getListCategoryApproved } from '@/api/product';

import SelectProducts from '@/components/products/common/select-data';

export default {
  name: 'EmissionFilter',
  components: { SelectProducts },
  props: {
    value: {
      type: Object,
      default: () => ({
        filterType: null,
        selectedData: [],
      }),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    branchIds: {
      type: Array,
      default: () => [],
    },
    isHasBranchNull: {
      type: Boolean,
      default: false,
    },
    productList: {
      type: Array,
      default: () => [],
    },
    categoryList: {
      type: Array,
      default: () => [],
    },
    isBubble: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filterTypeOptions: [...EMISSION_FILTER_TYPES],
      filterOptions: [],
    };
  },
  computed: {
    ...mapState('product', ['dashboard']),
    isVietnamese() {
      return i18n.locale === 'vi';
    },
    reactiveValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    labelFilterPlaceholder() {
      if ( this.reactiveValue.filterType === 1) {
        return {
          textPlaceholder: this.$t('cfp_radio_select_emissions_dashboard.placeholder_select_product'),
          textPlaceHolderInput: this.$t('cfp_radio_select_emissions_dashboard.text_search_product')
        }
      }else {
        return {
          textPlaceholder: this.$t('cfp_radio_select_emissions_dashboard.placeholder_select_category'),
          textPlaceHolderInput:  this.$t('cfp_radio_select_emissions_dashboard.text_search_category')
        }
      }
    },
  },
  methods: {
    async geFilterOptions() {
      const payload = {
        order_by_id: SORT_CATEGORY_BY_DESC,
        branch_ids: this.branchIds,
      };
      const isProd = this.reactiveValue.filterType === 1 ? true : false;
      this.filterOptions = [];
      const { data } = isProd ? await getListProductApproved(this.branchIds, this.isHasBranchNull) : await getListCategoryApproved(payload);

      this.filterOptions = (data || []).map((item) => {
        return {
          ...item,
          hasCheckbox: true,
          text: isProd ? `${item.name}/${item.lot_number || ''}` : `${item.name}`,
        };
      });
      const checkBoxAll = {
        hasCheckbox: true,
        text: isProd
          ? this.$t('cfp_radio_select_emissions_dashboard.check_box_select_all_products')
          : this.$t('cfp_radio_select_emissions_dashboard.check_box_select_all_category'),
      };

      this.filterOptions.unshift(checkBoxAll);
    },
    async handleFilterTypeChanged() {
      this.reactiveValue.selectedData = [];
      await this.$nextTick();
      await this.geFilterOptions();
      this.$emit('onChangeFilterType', this.reactiveValue.filterType);
      if (this.isBubble) this.$emit('onChangeFilterOptions', [...this.filterOptions]);
    },
    handleFilterOptionsSelected(value) {
      this.reactiveValue.selectedData = value || [];
      this.$emit('onChangeSelectedData', value);
    },
  },
  watch: {
    reactiveValue: {
      handler(newValue) {
        this.$emit('onChanged', newValue);
      },
      deep: true,
    },
    productList: {
      handler(newValue) {
        if (this.reactiveValue.filterType === 1) {
          this.filterOptions = [...newValue];
          this.$emit('onChangeFilterOptions', [...newValue]);
        }
      },
    },
  },
  mounted() {
    if (this.reactiveValue.filterType === 1) {
      this.filterOptions = this.productList;
    } else {
      this.filterOptions = this.categoryList;
    }
    this.$emit('onChangeFilterOptions', this.filterOptions);
  },
};
</script>

<style scoped lang="scss">
.emission-filter {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 40px;
  width: 100%;

  .product-radio {
    margin-top: 0;

    ::v-deep .v-input__control {
      .v-messages {
        display: none;
      }

      .v-input__slot {
        .radio-part {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 0px;
          gap: 16px;

          .v-radio {
            margin-bottom: 0;
            display: flex;
            flex-direction: row;
            align-items: center;
            padding: 0px;
            gap: 6px;

            .v-label {
              font-weight: 500;
              font-size: 14px;
              line-height: 24px;
              letter-spacing: 0.03em;
              color: $monoBlack;
            }
          }
        }
      }
    }
  }

  .emission-filter-select {
    width: 100%;
    padding-left: 20px;
    cursor: pointer;
  }
}

@include desktop {
  .emission-filter {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding: 0px;
    gap: 40px;
    width: 100%;

    .product-radio {
      margin-top: 0;
      padding-top: 0;

      ::v-deep .v-input__control {
        .v-messages {
          display: none;
        }

        .v-input__slot {
          .radio-part {
            display: flex;
            flex-direction: row;
            align-items: center;
            padding: 0px;
            gap: 40px;
            height: 40px;

            .v-radio {
              margin-bottom: 0;
              display: flex;
              flex-direction: row;
              align-items: center;
              padding: 0px;
              gap: 6px;
              .v-input--selection-controls__input {
                margin-top: 3px !important;
              }

              .v-label {
                font-weight: 500;
                font-size: 14px;
                line-height: 24px;
                letter-spacing: 0.03em;
                color: $monoBlack;
                white-space: nowrap;
              }
            }
          }
        }
      }
    }

    .emission-filter-select {
      display: flex;
      flex-grow: 1;
      min-width: 0;
      cursor: pointer;
      padding-left: 0;
    }
  }
}
</style>
